/**
 * AI Generate Senders Dropdown – group selector + amount + mode + generate
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Sparkles, ChevronDown, Layers, Copy, ChevronRight } from 'lucide-react';

import type { CountryAiMode } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/shared/multiCountryBulkEdit.types';
import { Button, LoadingSpinner, useModalPortalSurface } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';
import { cn } from '@/lib/utils';
import { useSenderPromptGroups } from '../hooks/useSenderPromptGroups';
import { useGenerateSenders } from '../hooks/useGenerateSenders';
import { AiPerDropDefaultGroupPanel } from './AiPerDropDefaultGroupPanel';
import { PerDropGroupSelector, type DropLabel } from './PerDropGroupSelector';

export type AiGenerateSendersMode = 'same' | 'perDrop';

interface AiGenerateSendersDropdownProps {
  countryKey: string;
  dropCount?: number;
  dropLabels?: DropLabel[];
  onOpen?: () => void;
  onGeneratingChange?: (isGenerating: boolean) => void;
  delegateGeneration?: boolean;
  creativeScope?: CountryAiMode;
  delegatedBusy?: boolean;
  onDelegatedGenerate?: (p: {
    groupId: string;
    amount: number;
    groupIdsPerDrop?: string[];
  }) => void | Promise<void>;
  onGenerated?: (
    senders: string[] | string[][],
    groupId: string,
    mode: AiGenerateSendersMode,
    durationMs?: number,
    groupIdsPerDrop?: string[],
  ) => void;
}

const MASTER_ID = 'master';
const AMOUNTS = Array.from({ length: 15 }, (_, i) => i + 1);

export function AiGenerateSendersDropdown({
  countryKey,
  dropCount = 1,
  dropLabels,
  onOpen,
  onGeneratingChange,
  delegateGeneration = false,
  creativeScope,
  delegatedBusy = false,
  onDelegatedGenerate,
  onGenerated,
}: AiGenerateSendersDropdownProps) {
  const { data: groups } = useSenderPromptGroups();
  const generate = useGenerateSenders();
  const modalPortalSurface = useModalPortalSurface();
  const { warning: toastWarning } = useToast();
  const [open, setOpen] = useState(false);
  const [groupId, setGroupId] = useState(MASTER_ID);
  const [amount, setAmount] = useState(4);
  const [mode, setMode] = useState<AiGenerateSendersMode>(() =>
    dropCount > 1 ? 'perDrop' : 'same',
  );
  const [groupIdsPerDrop, setGroupIdsPerDrop] = useState<string[]>([]);
  const [showPerDropConfig, setShowPerDropConfig] = useState(false);

  const activeGroups = groups?.filter((g) => g.is_active) ?? [];
  const isMulti = dropCount > 1;
  const perDropGroups = useMemo(
    () => isMulti && (delegateGeneration ? creativeScope === 'per_drop' : mode === 'perDrop'),
    [isMulti, delegateGeneration, creativeScope, mode],
  );
  const showApplyToToggle = isMulti && !delegateGeneration;
  const isBusy = delegateGeneration ? delegatedBusy : generate.isPending;

  useEffect(() => {
    onGeneratingChange?.(isBusy);
    return () => onGeneratingChange?.(false);
  }, [isBusy, onGeneratingChange]);

  const showApplyDefaultGroupToAllRows = useMemo(() => {
    if (!isMulti || dropCount < 1) return false;
    return [...Array(dropCount)].some(
      (_, i) => (groupIdsPerDrop[i] ?? groupId) !== groupId,
    );
  }, [isMulti, dropCount, groupIdsPerDrop, groupId]);

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (v) onOpen?.();
      setOpen(v);
    },
    [onOpen],
  );

  useEffect(() => {
    if (!perDropGroups) return;
    setGroupIdsPerDrop((prev) =>
      Array.from({ length: dropCount }, (_, i) => prev[i] ?? groupId),
    );
  }, [dropCount, perDropGroups, groupId]);

  useEffect(() => {
    if (!perDropGroups) setShowPerDropConfig(false);
  }, [perDropGroups]);

  const handleGenerate = useCallback(async () => {
    if (!groupId) return;
    const start = performance.now();
    const runPerDrop = isMulti && (delegateGeneration ? creativeScope === 'per_drop' : mode === 'perDrop');

    if (delegateGeneration && onDelegatedGenerate && creativeScope != null) {
      if (runPerDrop) {
        const ids =
          groupIdsPerDrop.length === dropCount
            ? groupIdsPerDrop
            : Array.from({ length: dropCount }, () => groupId);
        await onDelegatedGenerate({ groupId, amount, groupIdsPerDrop: ids });
      } else {
        await onDelegatedGenerate({ groupId, amount });
      }
      setOpen(false);
      return;
    }

    if (!onGenerated) return;

    if (runPerDrop) {
      const batches: string[][] = [];
      const ids =
        groupIdsPerDrop.length === dropCount
          ? groupIdsPerDrop
          : Array(dropCount).fill(groupId);
      for (let i = 0; i < dropCount; i++) {
        const gId = ids[i] ?? groupId;
        const data = await generate.mutateAsync({
          groupId: gId,
          countryKey,
          amount,
        });
        batches.push(data.senders);
      }
      if (batches.every((b) => b.length === 0)) {
        toastWarning(
          'No senders generated',
          'The AI returned no senders for any drop. Try again, lower the amount, or pick a different group.',
        );
        return;
      }
      const durationMs = Math.round(performance.now() - start);
      onGenerated(batches, groupId, 'perDrop', durationMs, ids);
      setOpen(false);
    } else {
      generate.mutate(
        { groupId, countryKey, amount },
        {
          onSuccess: (data) => {
            if (!data.senders.length) {
              toastWarning(
                'No senders generated',
                'The AI returned no senders. Try again, lower the amount, or pick a different group.',
              );
              return;
            }
            const durationMs = Math.round(performance.now() - start);
            onGenerated(data.senders, groupId, 'same', durationMs);
            setOpen(false);
          },
        },
      );
    }
  }, [
    groupId,
    countryKey,
    amount,
    mode,
    dropCount,
    isMulti,
    groupIdsPerDrop,
    generate,
    onGenerated,
    delegateGeneration,
    creativeScope,
    onDelegatedGenerate,
    toastWarning,
  ]);

  const totalSenders = isMulti && perDropGroups ? amount * dropCount : amount;
  const buttonLabel =
    isMulti && perDropGroups
      ? `Generate ${amount} × ${dropCount} senders`
      : `Generate ${totalSenders} sender${totalSenders !== 1 ? 's' : ''}`;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="ai-generate-senders-trigger"
          className={cn('h-7 gap-1 text-xs shrink-0', isBusy && 'pointer-events-none')}
          disabled={isBusy}
        >
          {isBusy ? (
            <LoadingSpinner className="h-3.5 w-3.5" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          AI
          <ChevronDown className="h-3 w-3" />
        </Button>
      </Popover.Trigger>

      <Popover.Portal {...(modalPortalSurface ? { container: modalPortalSurface } : {})}>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-[300] w-[min(540px,var(--radix-popover-content-available-width))] max-w-[95vw] rounded-lg border bg-card p-4 shadow-xl animate-in fade-in slide-in-from-top-2 duration-150"
        >
          <div className="space-y-3">
            {!(perDropGroups && showPerDropConfig) && (
              <div>
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Group
                </label>
                <select
                  className="h-8 w-full rounded-md border border-border bg-background px-2.5 text-xs"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  <option value={MASTER_ID}>Default</option>
                  {activeGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <label
                htmlFor="sender-amount"
                className="shrink-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
              >
                Amount
              </label>
              <select
                id="sender-amount"
                className="h-7 w-14 shrink-0 rounded-md border border-border bg-background px-2 text-xs"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              >
                {AMOUNTS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            {showApplyToToggle && (
              <div>
                <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Apply to
                </label>
                <div className="flex gap-1 rounded-md border border-border bg-muted/30 p-0.5">
                  <button
                    type="button"
                    onClick={() => setMode('same')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors',
                      mode === 'same'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Copy className="h-3 w-3" />
                    Same for all
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('perDrop')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors',
                      mode === 'perDrop'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Layers className="h-3 w-3" />
                    Per drop
                  </button>
                </div>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {mode === 'same'
                    ? `${amount} senders shared across ${dropCount} drops`
                    : `${amount} unique senders for each of ${dropCount} drops`}
                </p>
              </div>
            )}
            {perDropGroups && groups && (
              <div className={showApplyToToggle ? 'mt-2' : ''}>
                <button
                  type="button"
                  onClick={() => setShowPerDropConfig((v) => !v)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-2.5 py-1.5 text-left text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                >
                  <span className="flex items-center gap-1.5">
                    <Layers className="h-3 w-3" />
                    {showPerDropConfig ? 'Hide group per drop' : 'Configure group per drop'}
                  </span>
                  <ChevronRight
                    className={cn(
                      'h-3 w-3 shrink-0 transition-transform',
                      showPerDropConfig && 'rotate-90',
                    )}
                  />
                </button>
                {showPerDropConfig && (
                  <div className="mt-2 space-y-2">
                    <AiPerDropDefaultGroupPanel
                      masterId={MASTER_ID}
                      groupId={groupId}
                      onGroupIdChange={setGroupId}
                      activeGroups={activeGroups}
                      showApplyToAll={showApplyDefaultGroupToAllRows}
                      onApplyToAll={() =>
                        setGroupIdsPerDrop(Array.from({ length: dropCount }, () => groupId))
                      }
                    />
                    <PerDropGroupSelector
                      dropCount={dropCount}
                      groupIdsPerDrop={groupIdsPerDrop}
                      defaultGroupId={groupId}
                      groups={groups}
                      dropLabels={dropLabels}
                      onChange={setGroupIdsPerDrop}
                    />
                  </div>
                )}
              </div>
            )}
            <Button
              size="sm"
              className="h-8 w-full gap-1.5 text-xs"
              onClick={handleGenerate}
              disabled={
                !groupId ||
                isBusy ||
                (delegateGeneration && (!onDelegatedGenerate || creativeScope == null))
              }
            >
              {isBusy ? (
                <LoadingSpinner className="h-3 w-3" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              {buttonLabel}
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
